<template>
  <v-card>
    <v-card-title>
      {{ $t('rights.editForm.title') }}
      <v-spacer/>

      <v-switch v-model="fieldMode">
        <template v-slot:label>
          <v-tooltip top>
            <template v-slot:activator="{on, attrs}">
              <span v-on="on" v-bind="attrs">{{ $t('rights.editForm.allAtOnce') }}</span>
            </template>
            <span>{{ $t('rights.editForm.allAtOnceTip') }}</span>
          </v-tooltip>
        </template>
      </v-switch>
    </v-card-title>

    <v-card-subtitle v-show="fieldMode">
      {{ $t('rights.editForm.allAtOnceSubtitle', {len: items.length}) }}
    </v-card-subtitle>

    <v-container class="my-0 py-0">
      <v-row>
        <v-col cols="3" style="position: relative; overflow: auto" v-if="(!fieldMode) && (items.length > 1)">
          <v-list dense style="position: absolute; top: 0; left: 0; right: 0;">
            <v-list-item-group color="primary" :value="currentRightIndex">
              <v-list-item
                  v-for="(right, idx) in items"
                  :key="right.id"
                  :disabled="currentRightIndex === idx"
                  :class="{edited: idx in updatedItems}"
                  @click="selectItem(right, idx)"
                  two-line
              >
                <v-list-item-content>
                  <v-list-item-title>{{ right.isrc }}</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-tooltip bottom>
                      <template v-slot:activator="{on, attrs}">
                        <span v-on="on" v-bind="attrs">{{ right.artistName }} - {{ right.title }}</span>
                      </template>
                      <span>{{ right.artistName }} - {{ right.title }}</span>
                    </v-tooltip>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-col>

        <v-col class="mt-0" :cols="((!fieldMode) && items.length > 1) ? 9 : 12">
          <v-container class="my-0 py-0">
            <v-form ref="form">
              <v-row>
                <v-col cols="6">
                  <v-text-field
                      disabled
                      :label="$t('rights.columns.ifpiId')"
                  ></v-text-field>
                  <v-select
                      :items="[{text: $t('rights.types.audio'), value: '1'}, {text: $t('rights.types.video'), value: '2'}]"
                      :label="$t('rights.columns.type') + ' (*)'"
                      v-model="currentRight.type"
                  ></v-select>

                  <v-row no-gutters>
                    <v-col cols="10">
                      <NullableTextField
                          ref="isrcField"
                          v-model="currentRight.isrcnew"
                          :label="$t('rights.columns.isrc') + ' (*)'"
                          :nullable-mode="fieldMode"
                          :errors="getErrors('isrc')"
                          :itemId="currentRightIndex"
                      />
                    </v-col>
                    <v-col cols="2">
                      <v-sheet class="droparea ml-2"
                      outlined
                      rounded
                      height="64"
                      width="64"
                      :class="{ 'grey lighten-2': dragover }"
                      @drop.prevent="onDrop($event)"
                      @dragover.prevent="dragover = true"
                      @dragenter.prevent="dragover = true"
                      @dragleave.prevent="dragover = false"
                      >
                        <template v-if="hasBadIsrc && !fieldMode">
                          <v-icon v-if="gettingIsrc" class="ma-3" large :color="dragover ? 'grey darken-2' : '' ">mdi-loading mdi-spin</v-icon>
                          <v-icon v-else class="ma-3" large :color="dragover ? 'grey darken-2' : '' ">mdi-spotify</v-icon>
                        </template>
                        <v-icon v-else class="ma-3" large >mdi-circle-off-outline</v-icon>
                      </v-sheet>
                    </v-col>
                  </v-row>

                  <NullableTextField 
                      :label="$t('rights.columns.externalId')" 
                      v-model="currentRight.externalId"
                      :nullable-mode="fieldMode"
                      :itemId="currentRightIndex"
                  />

                  <NullableTextField
                      :label="$t('rights.columns.artistName') + ' (*)'"
                      v-model="currentRight.artistName"
                      :nullable-mode="fieldMode"
                      :errors="getErrors('arso')"
                      :itemId="currentRightIndex"
                  />
                  <NullableTextField
                      :label="$t('rights.columns.title') + ' (*)'"
                      v-model="currentRight.title"
                      :nullable-mode="fieldMode"
                      :errors="getErrors('tit')"
                      :itemId="currentRightIndex"
                  />
                  <NullableTextField
                      :label="$t('rights.columns.labelName') + ' (*)'"
                      v-model="currentRight.labelName"
                      :nullable-mode="fieldMode"
                      :errors="getErrors('labl')"
                      :itemId="currentRightIndex"
                  />
                  <NullableTextField
                      :label="$t('rights.columns.year')"
                      v-model="currentRight.year"
                      :nullable-mode="fieldMode"
                      :errors="getErrors('ccyy')"
                      :itemId="currentRightIndex"
                  />
                  <NullableAutocomplete
                      :label="$t('rights.columns.countryCode')"
                      v-model="currentRight.countryCode"
                      :nullable-mode="fieldMode"
                      :errors="getErrors('lndi')"
                      :itemId="currentRightIndex"
                      :rules="[rules.allowedIsoCountries]"
                  />
                  <NullableAutocomplete
                      :label="$t('rights.columns.companyCountryCode')"
                      v-model="currentRight.companyCountryCode"
                      :nullable-mode="fieldMode"
                      :errors="getErrors('lndcr')"
                      :itemId="currentRightIndex"
                      :rules="[rules.allowedIsoCountries]"
                  />
                  <NullableAutocomplete
                      :label="$t('rights.columns.secondaryCountryCode')"
                      v-model="currentRight.secondaryCountryCode"
                      :nullable-mode="fieldMode"
                      :errors="getErrors('lndsi')"
                      :itemId="currentRightIndex"
                      :rules="[rules.allowedIsoCountries]"
                  />
                </v-col>

                <v-col cols="6">
                  <v-switch :label="$t('rights.columns.revokeCol')" v-model="currentRight.revoke"></v-switch>

                  <template v-if="!currentRight.revoke">
                    <DatePickerField
                      ref="claimStartDateField" 
                      :label="$t('rights.columns.claimStartDate') + ' (*)'" 
                      v-model="currentRight.claimStartDate"
                      :errors="getErrors('fdat')"
                      :nullable-mode="fieldMode"
                      :itemId="currentRightIndex"
                    />
                    <DatePickerField
                      ref="claimEndDateField"
                      :label="$t('rights.columns.claimEndDate') + ' (*)'" 
                      v-model="currentRight.claimEndDate"
                      v-if="!currentRight.noEnd"
                      :errors="getErrors('tdat')"
                      :nullable-mode="fieldMode"
                      :itemId="currentRightIndex"
                    />
                    <v-switch class="mt-6" :label="$t('rights.columns.noEnd')" v-model="currentRight.noEnd"></v-switch>
                  </template>

                  <NullableTextField 
                    :label="$t('rights.columns.version')" 
                    v-model="currentRight.version"
                    :nullable-mode="fieldMode"
                    :itemId="currentRightIndex"
                  />
                  <NullableTextField 
                    :label="$t('rights.columns.duration')" 
                    v-model="currentRight.duration"
                    :nullable-mode="fieldMode"
                    :itemId="currentRightIndex"
                  />
                  <NullableTextField 
                    :label="$t('rights.columns.feat')" 
                    v-model="currentRight.feat"
                    :nullable-mode="fieldMode"
                    :itemId="currentRightIndex"
                  />
                  <NullableTextField 
                    :label="$t('rights.columns.searchTerms')" 
                    v-model="currentRight.searchTerms"
                    :nullable-mode="fieldMode"
                    :itemId="currentRightIndex"
                  />

                  <v-switch :label="$t('rights.columns.isClassical')" v-model="currentRight.isClassical"></v-switch>

                  <NullableTextField 
                    :label="$t('rights.columns.composer')" 
                    v-model="currentRight.composer"
                    :nullable-mode="fieldMode"
                    :itemId="currentRightIndex"
                  />
                  <v-switch :label="$t('rights.columns.internationalCollection')"
                            v-model="currentRight.internationalCollection"
                            v-if="$store.getters['app/currentRightsholder'].internationalCollectionAllowed"></v-switch>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-col>
      </v-row>
    </v-container>

    <v-divider/>
    <v-card-actions v-if="actionsLoading">
      <v-progress-linear :value="actionsLoadingProgress * 100" color="primary"
                         :indeterminate="actionsLoadingProgress < 0"></v-progress-linear>
    </v-card-actions>
    <v-card-actions v-else>
      <v-btn color="primary" text @click="onSave">{{ $t('rights.editForm.save') }}</v-btn>
      <v-spacer/>
      <v-btn color="secondary" text @click="close">{{ $t('rights.editForm.cancel') }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import DatePickerField from "@/components/common/ui/DatePickerField";
import Right from "@/models/Right";
import NullableTextField from "@/components/rights/NullableTextField";
import NullableAutocomplete from "@/components/rights/NullableAutocomplete";
import loFilter from 'lodash/filter'
import loGet from 'lodash/get'
import RulesMixin from '@/components/mixins/Rules'
import { mapActions } from "vuex";

export default {
  name: "EditRightForm",
  components: {NullableTextField, DatePickerField, NullableAutocomplete},
  props: {
    items: Array,
    actionsLoading: {
      type: Boolean,
      default: false
    },
    actionsLoadingProgress: {
      type: Number,
      default: -1
    }
  },
  data() {
    return {
      currentRightIndex: 0,
      updatedItems: [],
      fieldMode: false,
      fieldModeItem: null,
      dragover: false,
      gettingIsrc: false
    }
  },

  mixins: [RulesMixin],

  methods: {
    ...mapActions('app', ['getISRCForSpotifyLink']),

    onDrop(e) {
      this.dragover = false;
      if (!this.hasBadIsrc) {
        return
      }
      this.gettingIsrc = true;
      try {
        //- Only one - the "first" link
        const track = e.dataTransfer.getData("text").split('\n')[0]
        const validRe = /^https:\/\/open.spotify.com\/track\//i
        if (validRe.test(track)) {
          // API call
          this.getISRCForSpotifyLink(track)
            .then(async spotifyData => {
              this.gettingIsrc = false
              if (!spotifyData.isrc) {
                this.$toast(this.$t("staging.edit.addSpotifyDataError"))
                return 
              }
              const res = await this.$dialog.confirm({
                text: this.$t("staging.edit.addSpotifyData", spotifyData),
                title: this.$t("staging.edit.addSpotifyDataTitle"),
              });
              if (res) {
                // So ugly and took so long to fix... :-(
                const isrcTextField = loGet(this, '$refs.isrcField.$refs.textField', null)
                const inputField = isrcTextField.$el.getElementsByTagName('input')[0]
                const event = new Event('input')
                inputField.value = spotifyData.isrc
                inputField.dispatchEvent(event)              
              }
              this.gettingIsrc = false;
            })
            .catch((e) => {
              console.log(e)
              this.$toast(this.$t("staging.edit.addSpotifyDataError"))
              this.gettingIsrc = false;
            })
        } else {
          this.$toast(this.$t("staging.edit.addSpotifyLinkError"))
          this.gettingIsrc = false;
        }
      } catch (error) {
        this.gettingIsrc = false;
      }
    },

    getErrors(field) {
      if (this.currentRight && this.currentRight.error) {
        return loFilter(this.currentRight.error, ['field', field])
      }
      return [];
    },

    getUpdateTarget(right) {
      return new Right({...right, isrcnew: right.isrc});
    },

    selectItem(item, index) {
      if (index === this.currentRightIndex) {
        return;
      }
      if (!this.updatedItems[index]) {
        // duplicating item to edit
        this.updatedItems[index] = this.getUpdateTarget(item)
      }
      this.currentRightIndex = index;
    },

    dropSelection() {
      this.currentRightIndex = 0;
      this.updatedItems = [this.getUpdateTarget(this.items[0])];
      this.fieldModeItem = this.getUpdateTarget((Object.keys({...this.items[0]}).reduce((obj, key) => ({
        ...obj,
        [key]: null
      }), {})));
    },

    close() {
      this.dropSelection();
      this.$emit('close');
    },

    onSave() {
      //- BLUR dates to get correct values (if displayed)
      const sDate = loGet(this, '$refs.claimStartDateField.$refs.textField', null)
      const eDate = loGet(this, '$refs.claimEndDateField.$refs.textField', null)
      if (sDate) sDate.blur()
      if (eDate) eDate.blur()

      if (!this.$refs.form.validate()) {
        this.$dialog.error({
          // eslint-disable-next-line dot-notation
          text: this.$t('staging.edit.countryValidation'),
          icon: false
        })
        return
      }

      if (this.fieldMode) {
        const editResult = Object.entries(this.fieldModeItem).reduce((res, [k, v]) => {
          // eslint-disable-next-line no-prototype-builtins
          if (this.fieldModeItem.hasOwnProperty(k)) {
            // eslint-disable-next-line no-undefined
            if (![null, undefined].includes(v)) {
              res[k] = v;
            }
          }
          return res;
        }, {});
        this.$emit('save', {
          mode: 'field',
          item: editResult,
          targets: this.items,
        })
      } else {
        this.$emit('save', {
          mode: 'list',
          items: this.updatedItems.reduce((arr, x) => arr.concat(x), [])
        })
      }
    }
  },
  computed: {
    currentRight() {
      return this.fieldMode ? this.fieldModeItem : this.updatedItems[this.currentRightIndex];
    },

    hasBadIsrc() {
      const errors = loGet(this, 'currentRight.error', [])
      return Boolean(errors && errors.find(e => {return e.field == 'isrc'}))
    },

    messages() {
      const messages = {};
      if (this.fieldMode) {
        for (let k in this.fieldModeItem) {
          if (this.fieldModeItem[k] === null) {
            messages[k] = [this.$t('rights.editForm.noChanges')]
          }
        }

      }
      return messages;

    }
  },
  watch: {
    items() {
      this.dropSelection()
    }
  },
  mounted() {
    this.currentRightIndex = 0;
  },
  created() {
    this.dropSelection();
  }
}
</script>

<style scoped lang="scss">
.v-list-item.edited:not(.v-list-item--active) {
  background-color: #ffffcc;

  .v-list-item__title {
    color: #770;
  }
}
</style>